import type { ApiResponse, StripeSettings, SmsSettings, WebrtcSettings, TwilioVoiceSettings, TurnstileSettings, SystemSettings, WelcomeBannerSettings } from '@klaro7/shared-types';
import type { UpdateServicesSettingsInput, UpdateStripeSettingsInput, UpdateSmsSettingsInput, UpdateWebrtcSettingsInput, UpdateTwilioVoiceSettingsInput, UpdateTurnstileSettingsInput, UpdatePhoneLookupSettingsInput, UpdateRegistrationCountryInput, UpdateWelcomeBannerInput } from '@klaro7/validators';

import api from '@/lib/api';

export interface OpenAIModel {
  id: string;
  owned_by: string;
}

export async function getServicesSettings(): Promise<SystemSettings> {
  const response = await api.get<ApiResponse<SystemSettings>>('/settings/services');
  return response.data.data;
}

export async function updateServicesSettings(
  data: UpdateServicesSettingsInput,
): Promise<SystemSettings> {
  const response = await api.put<ApiResponse<SystemSettings>>('/settings/services', data);
  return response.data.data;
}

export async function getOpenAIModels(): Promise<OpenAIModel[]> {
  const response = await api.get<ApiResponse<OpenAIModel[]>>('/settings/openai/models');
  return response.data.data;
}

export interface OpenAIChatTestResult {
  success: boolean;
  response?: string;
  model?: string;
  error?: string;
}

export async function testOpenAIChat(): Promise<OpenAIChatTestResult> {
  const response = await api.post<ApiResponse<OpenAIChatTestResult>>('/settings/openai/test-chat');
  return response.data.data;
}

export interface OpenAIWhisperTestResult {
  success: boolean;
  error?: string;
}

export async function testOpenAIWhisper(): Promise<OpenAIWhisperTestResult> {
  const response = await api.post<ApiResponse<OpenAIWhisperTestResult>>('/settings/openai/test-whisper');
  return response.data.data;
}

export interface GeminiModel {
  id: string;
  displayName: string;
}

export async function getGeminiModels(): Promise<GeminiModel[]> {
  const response = await api.get<ApiResponse<GeminiModel[]>>('/settings/gemini/models');
  return response.data.data;
}

export interface GeminiTranscriptionTestResult {
  success: boolean;
  model?: string;
  error?: string;
}

export async function testGeminiTranscription(): Promise<GeminiTranscriptionTestResult> {
  const response = await api.post<ApiResponse<GeminiTranscriptionTestResult>>('/settings/gemini/test-transcription');
  return response.data.data;
}

export interface SmtpTestResult {
  success: boolean;
  error?: string;
}

export async function testSmtpConnection(): Promise<SmtpTestResult> {
  const response = await api.post<ApiResponse<SmtpTestResult>>('/settings/smtp/test');
  return response.data.data;
}

export interface TurnTestResult {
  ok: boolean;
  relayAddress?: string;
  reason?: 'not_configured' | 'auth' | 'unreachable';
}

export async function testTurn(): Promise<TurnTestResult> {
  const response = await api.post<ApiResponse<TurnTestResult>>('/settings/webrtc/test');
  return response.data.data;
}

export interface RegistrationStatus {
  registrationEnabled: boolean;
  waitlistEnabled: boolean;
}

export async function getRegistrationStatus(): Promise<RegistrationStatus> {
  const response = await api.get<ApiResponse<RegistrationStatus>>('/settings/registration-status');
  return response.data.data;
}

// ---- Feature flags (runtime DB-backed config) ----

export type FeatureFlags = Record<string, unknown>;

export async function getFeatureFlags(): Promise<FeatureFlags> {
  const response = await api.get<ApiResponse<FeatureFlags>>('/settings/feature-flags');
  return response.data.data;
}

export async function setFeatureFlag(key: string, value: unknown): Promise<FeatureFlags> {
  const response = await api.put<ApiResponse<FeatureFlags>>(
    `/settings/feature-flags/${encodeURIComponent(key)}`,
    { value },
  );
  return response.data.data;
}

export async function deleteFeatureFlag(key: string): Promise<FeatureFlags> {
  const response = await api.delete<ApiResponse<FeatureFlags>>(
    `/settings/feature-flags/${encodeURIComponent(key)}`,
  );
  return response.data.data;
}

// ---- Config status (manifest of expected runtime config keys) ----

export interface ConfigKeyStatus {
  key: string;
  label: string;
  suggestion: string;
  description: string;
  required: boolean;
  configured: boolean;
}

export async function getConfigStatus(): Promise<ConfigKeyStatus[]> {
  const response = await api.get<ApiResponse<ConfigKeyStatus[]>>('/settings/config-status');
  return response.data.data;
}

// ---- Config reachability test ----

export interface ConfigTestResult {
  ok: boolean;
  status?: number;
  ms: number;
  message: string;
}

export async function testConfig(key: string): Promise<ConfigTestResult> {
  const response = await api.post<ApiResponse<ConfigTestResult>>(
    '/settings/feature-flags/test',
    { key },
  );
  return response.data.data;
}

// ---- Stripe config (super-admin only) ----

export async function getStripeSettings(): Promise<StripeSettings> {
  const response = await api.get<ApiResponse<StripeSettings>>('/settings/stripe');
  return response.data.data;
}

export async function updateStripeSettings(
  data: UpdateStripeSettingsInput,
): Promise<StripeSettings> {
  const response = await api.put<ApiResponse<StripeSettings>>('/settings/stripe', data);
  return response.data.data;
}

export interface StripeTestResult {
  ok: boolean;
  accountId?: string;
  message?: string;
}

export async function testStripe(
  data: { secretKey?: string } = {},
): Promise<StripeTestResult> {
  const response = await api.post<ApiResponse<StripeTestResult>>('/settings/stripe/test', data);
  return response.data.data;
}

// ---- OVH SMS config (super-admin only) ----

export async function getSmsSettings(): Promise<SmsSettings> {
  const response = await api.get<ApiResponse<SmsSettings>>('/settings/sms');
  return response.data.data;
}

export async function updateSmsSettings(
  data: UpdateSmsSettingsInput,
): Promise<SmsSettings> {
  const response = await api.put<ApiResponse<SmsSettings>>('/settings/sms', data);
  return response.data.data;
}

export interface SmsTestResult {
  sid: string;
  status: string;
  to: string;
  from: string;
  creditsLeft?: number;
}

export async function sendTestSms(
  data: { to: string; body?: string },
): Promise<SmsTestResult> {
  const response = await api.post<ApiResponse<SmsTestResult>>('/settings/sms/test', data);
  return response.data.data;
}

// ---- WebRTC / TURN config (super-admin only) ----

export async function getWebrtcSettings(): Promise<WebrtcSettings> {
  const response = await api.get<ApiResponse<WebrtcSettings>>('/settings/webrtc');
  return response.data.data;
}

export async function updateWebrtcSettings(
  data: UpdateWebrtcSettingsInput,
): Promise<WebrtcSettings> {
  const response = await api.put<ApiResponse<WebrtcSettings>>('/settings/webrtc', data);
  return response.data.data;
}

// ---- Cloudflare Turnstile (super-admin only) ----

export async function getTurnstileSettings(): Promise<TurnstileSettings> {
  const response = await api.get<ApiResponse<TurnstileSettings>>('/settings/turnstile');
  return response.data.data;
}

export async function updateTurnstileSettings(
  data: UpdateTurnstileSettingsInput,
): Promise<TurnstileSettings> {
  const response = await api.put<ApiResponse<TurnstileSettings>>('/settings/turnstile', data);
  return response.data.data;
}

// ---- Phone Lookup / VoIP detection (super-admin only) ----

export interface PhoneLookupSettings {
  enabled: boolean;
}

export interface PhoneLookupStats {
  month: string;
  items: Array<{ lineType: string; count: number; estCostUsd: number }>;
  total: { count: number; estCostUsd: number };
}

export async function getPhoneLookupSettings(): Promise<PhoneLookupSettings> {
  const response = await api.get<ApiResponse<PhoneLookupSettings>>('/settings/phone-lookup');
  return response.data.data;
}

export async function updatePhoneLookupSettings(
  data: UpdatePhoneLookupSettingsInput,
): Promise<PhoneLookupSettings> {
  const response = await api.put<ApiResponse<PhoneLookupSettings>>('/settings/phone-lookup', data);
  return response.data.data;
}

export async function getPhoneLookupStats(): Promise<PhoneLookupStats> {
  const response = await api.get<ApiResponse<PhoneLookupStats>>('/settings/phone-lookup/stats');
  return response.data.data;
}

// ---- Allow-list paesi in registrazione (super-admin only) ----

export interface RegistrationCountrySettings {
  enabled: boolean;
  allowedDials: string[];
}

export async function getRegistrationCountrySettings(): Promise<RegistrationCountrySettings> {
  const response = await api.get<ApiResponse<RegistrationCountrySettings>>('/settings/registration-country');
  return response.data.data;
}

export async function updateRegistrationCountrySettings(
  data: UpdateRegistrationCountryInput,
): Promise<RegistrationCountrySettings> {
  const response = await api.put<ApiResponse<RegistrationCountrySettings>>('/settings/registration-country', data);
  return response.data.data;
}

// ---- Twilio Voice config (super-admin only) ----

export async function getTwilioVoiceSettings(): Promise<TwilioVoiceSettings> {
  const response = await api.get<ApiResponse<TwilioVoiceSettings>>('/settings/twilio-voice');
  return response.data.data;
}

export async function updateTwilioVoiceSettings(
  data: UpdateTwilioVoiceSettingsInput,
): Promise<TwilioVoiceSettings> {
  const response = await api.put<ApiResponse<TwilioVoiceSettings>>('/settings/twilio-voice', data);
  return response.data.data;
}

export interface VoiceTestResult {
  sid: string;
  status: string;
  to: string;
  from: string;
}

export async function sendTestVoiceCall(
  data: { to: string; message?: string },
): Promise<VoiceTestResult> {
  const response = await api.post<ApiResponse<VoiceTestResult>>('/settings/twilio-voice/test', data);
  return response.data.data;
}

// ---- Welcome banner (super-admin managed) ----

export async function getWelcomeBanner(): Promise<WelcomeBannerSettings> {
  const response = await api.get<ApiResponse<WelcomeBannerSettings>>('/settings/welcome-banner');
  return response.data.data;
}

/** Public endpoint — consumed by the banner itself, no auth. */
export async function getWelcomeBannerPublic(): Promise<WelcomeBannerSettings> {
  const response = await api.get<ApiResponse<WelcomeBannerSettings>>('/settings/welcome-banner/public');
  return response.data.data;
}

export async function updateWelcomeBanner(
  data: UpdateWelcomeBannerInput,
): Promise<WelcomeBannerSettings> {
  const response = await api.put<ApiResponse<WelcomeBannerSettings>>('/settings/welcome-banner', data);
  return response.data.data;
}

export async function uploadWelcomeBannerImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post<ApiResponse<{ url: string }>>(
    '/settings/welcome-banner/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.data.url;
}

// ---- Social links (7 piattaforme fisse) ----

export type SocialPlatform =
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'x'
  | 'telegram'
  | 'whatsapp';

/** Ordine fisso di render lato pubblico. */
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'x',
  'telegram',
  'whatsapp',
];

export interface SocialLinkConfig {
  enabled: boolean;
  url: string;
}

/** Tutte e 7 le piattaforme sempre presenti (admin, GET/PUT /settings/social). */
export type SocialSettings = Record<SocialPlatform, SocialLinkConfig>;

/** Solo le piattaforme visibili (enabled + url valido), già ordinate dal server. */
export interface PublicSocialLink {
  platform: SocialPlatform;
  url: string;
}

export interface PublicConfig {
  socketUrl?: string;
  social: PublicSocialLink[];
  /** Google Ads tag id (AW-…) — null se disattivo o non configurato. */
  googleAdsTagId?: string | null;
  /**
   * Destinazione della conversione "Acquisto" già composta (`AW-<id>/<label>`)
   * — null se il tag è spento o l'etichetta non è configurata.
   */
  googleAdsPurchaseSendTo?: string | null;
  /** Meta Pixel id (numerico) — null se disattivo o non configurato. */
  metaPixelId?: string | null;
  /** Flag eventi Meta Pixel — null se il pixel non è pubblico. */
  metaPixelEvents?: { pageView: boolean; completeRegistration: boolean; purchase: boolean } | null;
  /** Cloudflare Turnstile site key per il widget di registrazione — null se disattivo. */
  turnstileSiteKey?: string | null;
}

/** GET /settings/public-config (pubblico, no auth) — usato dai componenti React montati. */
export async function getPublicConfig(): Promise<PublicConfig> {
  const response = await api.get<ApiResponse<PublicConfig>>('/settings/public-config');
  return response.data.data;
}

/** GET /settings/social (super-admin only). */
export async function getSocialSettings(): Promise<SocialSettings> {
  const response = await api.get<ApiResponse<SocialSettings>>('/settings/social');
  return response.data.data;
}

/** PUT /settings/social (super-admin only). */
export async function updateSocialSettings(data: SocialSettings): Promise<SocialSettings> {
  const response = await api.put<ApiResponse<SocialSettings>>('/settings/social', data);
  return response.data.data;
}

// ---- Google Ads tag (super-admin only) ----

export interface GoogleAdsSettings {
  enabled: boolean;
  tagId: string;
  /** Etichetta dell'azione di conversione "Acquisto"; '' = nessuna conversione. */
  purchaseLabel?: string;
}

/** GET /settings/google-ads (super-admin only). */
export async function getGoogleAdsSettings(): Promise<GoogleAdsSettings> {
  const response = await api.get<ApiResponse<GoogleAdsSettings>>('/settings/google-ads');
  return response.data.data;
}

/** PUT /settings/google-ads (super-admin only). */
export async function updateGoogleAdsSettings(data: GoogleAdsSettings): Promise<GoogleAdsSettings> {
  const response = await api.put<ApiResponse<GoogleAdsSettings>>('/settings/google-ads', data);
  return response.data.data;
}

// ---- Meta Pixel (super-admin only) ----

export interface MetaPixelSettings {
  enabled: boolean;
  pixelId: string;
  events: { pageView: boolean; completeRegistration: boolean; purchase: boolean };
}

/** GET /settings/meta-pixel (super-admin only). */
export async function getMetaPixelSettings(): Promise<MetaPixelSettings> {
  const response = await api.get<ApiResponse<MetaPixelSettings>>('/settings/meta-pixel');
  return response.data.data;
}

/** PUT /settings/meta-pixel (super-admin only). */
export async function updateMetaPixelSettings(data: MetaPixelSettings): Promise<MetaPixelSettings> {
  const response = await api.put<ApiResponse<MetaPixelSettings>>('/settings/meta-pixel', data);
  return response.data.data;
}
